<script setup>
import { computed, reactive, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useSidebar } from '@/composables/useSidebar';
import { useBranding } from '@/branding';
import { APPEARANCE_OPTIONS, useAppearance } from '@/composables/useAppearance';

// Sidebar in the style of the Laravel React starter kit (shadcn/ui sidebar,
// "inset" variant, collapsible to icons): brand at the top, one navigation
// group, and the signed-in user at the bottom.
const props = defineProps({
  // [{ title, icon, route, subLink?: [{ title, route }] }]
  links: { type: Array, default: () => [] },
  // Route prefix for this role, e.g. "/admin".
  basePath: { type: String, required: true },
  user: { type: Object, required: true }, // { name, email, avatar }
  // Roles the user can switch between: [{ id, description }]. The switcher
  // under the brand shows when there is more than one.
  roles: { type: Array, default: () => [] },
  currentRoleId: { type: Number, default: null },
  switchingRole: { type: Boolean, default: false },
  // Number pills by menu route, e.g. { '/accounts': 3 }. Zero shows nothing.
  badges: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['switch-role', 'logout']);

const currentRole = computed(
  () => props.roles.find((r) => r.id === props.currentRoleId) ?? null,
);

const branding = useBranding();
const appearance = useAppearance();
const route = useRoute();
const { state, isMobile, collapsed, setOpen, setOpenMobile } = useSidebar();

// Which collapsible groups are expanded, keyed by title.
const openGroups = reactive({});

const drawerOpen = computed(() => (isMobile.value ? state.openMobile : true));

// A photo that fails to load falls back to the initials.
const avatarFailed = shallowRef(false);
watch(() => props.user.avatar, () => (avatarFailed.value = false));

const initials = computed(() =>
  (props.user.name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join(''),
);

function href(path) {
  return `${props.basePath}${path}`;
}

// The page or one below it, by whole path segments: /students must not
// match /students-records.
function isOnPage(path) {
  return route.path === path || route.path.startsWith(`${path}/`);
}

const badgeFor = (path) => Number(props.badges[path]) || 0;
// A closed group shows the total of its items, so the count isn't hidden.
const groupBadge = (link) => (link.subLink || []).reduce((n, sub) => n + badgeFor(sub.route), 0);
const badgeText = (n) => (n > 99 ? '99+' : String(n));
const tipFor = (title, n) => (n ? `${title} (${n})` : title);

function isGroupActive(link) {
  return (link.subLink || []).some((sub) => isOnPage(href(sub.route)));
}

function toggleGroup(link) {
  // In the icon rail there's no room for sub-items: open the sidebar first.
  if (collapsed.value) {
    setOpen(true);
    openGroups[link.title] = true;
    return;
  }
  openGroups[link.title] = !openGroups[link.title];
}

function onDrawerUpdate(value) {
  if (isMobile.value) setOpenMobile(value);
}

// The mobile panel closes once a page is chosen.
watch(
  () => route.fullPath,
  () => {
    if (isMobile.value) setOpenMobile(false);
  },
);

// Keep the group containing the current page expanded.
watch(
  () => [route.path, props.links],
  () => {
    for (const link of props.links) {
      if (link.subLink && isGroupActive(link)) openGroups[link.title] = true;
    }
  },
  { immediate: true },
);
</script>

<template>
  <v-navigation-drawer
    :model-value="drawerOpen"
    :temporary="isMobile"
    :permanent="!isMobile"
    :rail="collapsed"
    :rail-width="64"
    :width="isMobile ? 288 : 256"
    floating
    class="app-sidebar"
    @update:model-value="onDrawerUpdate"
  >
    <div class="sb" :class="{ 'sb--collapsed': collapsed }">
      <!-- Header: school brand -->
      <div class="sb__section">
        <router-link :to="href('/dashboard')" class="sb-btn sb-btn--lg">
          <span class="sb-logo">
            <img :src="branding.logoUrl" alt="" />
          </span>
          <span class="sb-text">
            <span class="sb-text__title">{{ branding.shortName }}</span>
            <span class="sb-text__subtitle">Class Management</span>
          </span>
        </router-link>

        <!-- Role switcher (shadcn "team switcher") -->
        <v-menu
          v-if="roles.length > 1"
          :location="collapsed && !isMobile ? 'end top' : 'bottom start'"
          offset="6"
        >
          <template #activator="{ props: menuProps, isActive }">
            <button
              v-bind="menuProps"
              type="button"
              class="sb-btn sb-role"
              :class="{ 'is-open': isActive }"
              aria-label="Switch role"
            >
              <span class="sb-role__icon">
                <v-progress-circular v-if="switchingRole" indeterminate size="14" width="2" />
                <v-icon v-else size="16">mdi-account-switch-outline</v-icon>
              </span>
              <span class="sb-text">
                <span class="sb-role__label">Role</span>
                <span class="sb-role__name">{{ currentRole?.description || 'Choose a role' }}</span>
              </span>
              <v-icon class="sb-updown">mdi-unfold-more-horizontal</v-icon>
              <v-tooltip v-if="collapsed" activator="parent" location="end" text="Switch role" />
            </button>
          </template>

          <div class="user-menu" role="menu" aria-label="Switch role">
            <div class="user-menu__heading">Switch role</div>
            <button
              v-for="role in roles"
              :key="role.id"
              type="button"
              role="menuitemradio"
              class="user-menu__item"
              :aria-checked="role.id === currentRoleId"
              :disabled="switchingRole"
              @click="role.id !== currentRoleId && emit('switch-role', role.id)"
            >
              <v-icon>mdi-account-outline</v-icon>
              {{ role.description }}
              <v-icon v-if="role.id === currentRoleId" class="user-menu__check">mdi-check</v-icon>
            </button>
          </div>
        </v-menu>
      </div>

      <!-- Navigation -->
      <nav class="sb__content" aria-label="Main">
        <div class="sb-group">
          <div class="sb-group__label">Platform</div>
          <ul class="sb-menu">
            <li v-for="link in links" :key="link.title" class="sb-menu__item">
              <router-link
                v-if="!link.subLink"
                :to="href(link.route)"
                class="sb-btn"
                active-class="is-active"
              >
                <v-icon class="sb-icon">{{ link.icon }}</v-icon>
                <span class="sb-label">{{ link.title }}</span>
                <span v-if="badgeFor(link.route)" class="sb-badge" :aria-label="`${badgeFor(link.route)} waiting`">
                  {{ badgeText(badgeFor(link.route)) }}
                </span>
                <v-tooltip
                  v-if="collapsed"
                  activator="parent"
                  location="end"
                  :text="tipFor(link.title, badgeFor(link.route))"
                />
              </router-link>

              <template v-else>
                <button
                  type="button"
                  class="sb-btn"
                  :class="{ 'is-active': collapsed && isGroupActive(link) }"
                  :aria-expanded="!!openGroups[link.title]"
                  @click="toggleGroup(link)"
                >
                  <v-icon class="sb-icon">{{ link.icon }}</v-icon>
                  <span class="sb-label">{{ link.title }}</span>
                  <span
                    v-if="groupBadge(link) && (collapsed || !openGroups[link.title])"
                    class="sb-badge"
                    :aria-label="`${groupBadge(link)} waiting`"
                  >
                    {{ badgeText(groupBadge(link)) }}
                  </span>
                  <v-icon
                    class="sb-chevron"
                    :class="{ 'sb-chevron--open': openGroups[link.title] }"
                  >
                    mdi-chevron-right
                  </v-icon>
                  <v-tooltip
                    v-if="collapsed"
                    activator="parent"
                    location="end"
                    :text="tipFor(link.title, groupBadge(link))"
                  />
                </button>
                <ul
                  v-show="openGroups[link.title] && !collapsed"
                  class="sb-sub"
                >
                  <li v-for="sub in link.subLink" :key="sub.title">
                    <router-link
                      :to="href(sub.route)"
                      class="sb-sub__btn"
                      active-class="is-active"
                    >
                      <span class="sb-label">{{ sub.title }}</span>
                      <span v-if="badgeFor(sub.route)" class="sb-badge" :aria-label="`${badgeFor(sub.route)} waiting`">
                        {{ badgeText(badgeFor(sub.route)) }}
                      </span>
                    </router-link>
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
      </nav>

      <!-- Footer: signed-in user -->
      <div class="sb__section">
        <v-menu
          :location="isMobile ? 'top' : collapsed ? 'end bottom' : 'top'"
          offset="8"
        >
          <template #activator="{ props: menuProps, isActive }">
            <button
              v-bind="menuProps"
              type="button"
              class="sb-btn sb-btn--lg"
              :class="{ 'is-open': isActive }"
              aria-label="Account menu"
            >
              <span class="sb-avatar">
                <img v-if="user.avatar && !avatarFailed" :src="user.avatar" alt="" @error="avatarFailed = true" />
                <span v-else>{{ initials }}</span>
              </span>
              <span class="sb-text">
                <span class="sb-text__name">{{ user.name }}</span>
              </span>
              <v-icon class="sb-updown">mdi-unfold-more-horizontal</v-icon>
            </button>
          </template>

          <div class="user-menu">
            <div class="user-menu__label">
              <span class="sb-avatar">
                <img v-if="user.avatar && !avatarFailed" :src="user.avatar" alt="" @error="avatarFailed = true" />
                <span v-else>{{ initials }}</span>
              </span>
              <span class="sb-text">
                <span class="sb-text__name">{{ user.name }}</span>
                <span v-if="user.email" class="sb-text__email">
                  {{ user.email }}
                </span>
              </span>
            </div>
            <div class="user-menu__sep" />
            <!-- Theme ▸ Light / Dark / System (shadcn sub-menu) -->
            <v-menu
              :location="isMobile ? 'bottom' : 'end top'"
              offset="8"
              open-on-hover
              open-on-click
              :open-delay="0"
            >
              <template #activator="{ props: subProps, isActive: subOpen }">
                <button
                  v-bind="subProps"
                  type="button"
                  class="user-menu__item"
                  :class="{ 'is-open': subOpen }"
                >
                  <v-icon>mdi-palette-outline</v-icon>
                  Theme
                  <v-icon class="user-menu__chevron">mdi-chevron-right</v-icon>
                </button>
              </template>
              <div class="user-menu user-menu--sub" role="menu" aria-label="Theme">
                <button
                  v-for="option in APPEARANCE_OPTIONS"
                  :key="option.value"
                  type="button"
                  role="menuitemradio"
                  class="user-menu__item"
                  :aria-checked="appearance.mode.value === option.value"
                  @click="appearance.setMode(option.value)"
                >
                  <span class="user-menu__dot">
                    <span v-if="appearance.mode.value === option.value" />
                  </span>
                  <v-icon>{{ option.icon }}</v-icon>
                  {{ option.title }}
                </button>
              </div>
            </v-menu>
            <router-link :to="href('/settings')" class="user-menu__item">
              <v-icon>mdi-cog-outline</v-icon>
              Settings
            </router-link>
            <div class="user-menu__sep" />
            <button
              type="button"
              class="user-menu__item"
              @click="emit('logout')"
            >
              <v-icon>mdi-logout</v-icon>
              Log out
            </button>
          </div>
        </v-menu>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style scoped>
/* shadcn "neutral" sidebar tokens */
.app-sidebar {
  --sb-bg: var(--sidebar);
  --sb-fg: var(--foreground);
  --sb-muted: var(--muted-foreground);
  --sb-accent: var(--accent);
  --sb-border: var(--border);
  background-color: var(--sb-bg) !important;
  border: 0 !important;
  color: var(--sb-fg);
}

.sb {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
  gap: 8px;
}

.sb__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sb__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Menu buttons (SidebarMenuButton) */
.sb-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 0 10px;
  gap: 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--sb-fg);
  font: inherit;
  font-size: 15px;
  line-height: 1;
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s ease, width 0.2s linear,
    height 0.2s linear, padding 0.2s linear;
}
.sb-btn:hover,
.sb-btn.is-open {
  background-color: var(--sb-accent);
}
.sb-btn.is-active {
  background-color: var(--brand-soft);
  color: var(--brand);
  font-weight: 500;
}
.sb-btn.is-active .sb-icon {
  color: var(--brand);
}
.sb-btn:focus-visible,
.sb-sub__btn:focus-visible,
.user-menu__item:focus-visible {
  box-shadow: 0 0 0 2px var(--faint-foreground);
}

.sb-btn--lg {
  height: 52px;
  padding: 0 8px;
}

.sb-icon {
  flex-shrink: 0;
  font-size: 20px !important;
  width: 20px;
  height: 20px;
  color: var(--secondary-foreground);
}

.sb-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sb-chevron {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 18px !important;
  color: var(--sb-muted);
  transition: transform 0.2s ease;
}
.sb-chevron--open {
  transform: rotate(90deg);
}

/* Number pill (shadcn SidebarMenuBadge) in the school's colour, e.g.
   registrations waiting */
.sb-btn {
  position: relative;
}
.sb-badge {
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 9999px;
  background: var(--brand);
  color: var(--brand-foreground);
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 20px;
  text-align: center;
}
.sb-sub__btn .sb-badge {
  margin-left: 8px;
}

/* Brand tile */
.sb-logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
}
.sb-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sb-text {
  display: grid;
  flex: 1;
  min-width: 0;
  text-align: left;
  font-size: 15px;
  line-height: 1.25;
}
.sb-text__title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sb-text__subtitle,
.sb-text__email {
  font-size: 13px;
  color: var(--sb-muted);
  overflow: hidden;
  text-overflow: ellipsis;
}
.sb-text__name {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sb-updown {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 16px !important;
}

/* Avatar: photo, or initials on neutral */
.sb-avatar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  overflow: hidden;
  background-color: var(--border);
  color: var(--foreground);
  font-size: 12px;
  font-weight: 500;
}
.sb-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Group (SidebarGroup) */
.sb-group__label {
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 10px;
  font-size: 13px;
  font-weight: 500;
  color: color-mix(in srgb, var(--foreground) 70%, transparent);
  white-space: nowrap;
  transition: margin 0.2s linear, opacity 0.2s linear;
}

.sb-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Sub items (SidebarMenuSub) */
.sb-sub {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 19px 0;
  padding: 2px 10px;
  border-left: 1px solid var(--sb-border);
  list-style: none;
}
.sb-sub__btn {
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  color: var(--sb-fg);
  font-size: 15px;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  outline: none;
}
.sb-sub__btn:hover {
  background-color: var(--sb-accent);
}
.sb-sub__btn.is-active {
  background-color: var(--brand-soft);
  color: var(--brand);
  font-weight: 500;
}

/* Icon rail: the pill shrinks to a dot on the icon */
.sb--collapsed .sb-badge {
  position: absolute;
  top: 7px;
  right: 7px;
  min-width: 0;
  width: 8px;
  height: 8px;
  padding: 0;
  font-size: 0;
  box-shadow: 0 0 0 2px var(--sidebar);
}
.sb--collapsed .sb-btn {
  width: 40px;
  padding: 0;
  justify-content: center;
  margin: 0 auto;
}
.sb--collapsed .sb-btn--lg {
  width: 40px;
  height: 40px;
}
.sb--collapsed .sb-label,
.sb--collapsed .sb-text,
.sb--collapsed .sb-chevron,
.sb--collapsed .sb-updown {
  display: none;
}
.sb--collapsed .sb-group__label {
  margin-top: -32px;
  opacity: 0;
}

/* User dropdown (DropdownMenuContent) */
.user-menu {
  min-width: 240px;
  padding: 4px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.user-menu__label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
}
.user-menu__sep {
  height: 1px;
  margin: 4px -4px;
  background-color: var(--border);
}
.user-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-size: 15px;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  outline: none;
}
.user-menu__item:hover,
.user-menu__item.is-open {
  background-color: var(--accent);
}
.user-menu__heading {
  padding: 6px 10px 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.user-menu__check {
  margin-left: auto;
}
.user-menu__item:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Role switcher under the brand */
.sb-role {
  height: 48px;
  border: 1px solid var(--sb-border);
  background: var(--card);
}
.sb-role__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--brand-soft);
  color: var(--brand);
}
.sb-role__label {
  font-size: 11px;
  line-height: 1.2;
  color: var(--sb-muted);
}
.sb-role__name {
  overflow: hidden;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sb--collapsed .sb-role {
  border-color: transparent;
  background: transparent;
}

.user-menu--sub {
  min-width: 160px;
}
.user-menu__chevron {
  margin-left: auto;
}
/* Radio indicator for the selected theme */
.user-menu__dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  margin-right: -2px;
}
.user-menu__dot span {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background-color: var(--foreground);
}
.user-menu__item .v-icon {
  font-size: 18px !important;
  color: var(--muted-foreground);
}

@media (prefers-reduced-motion: reduce) {
  .sb-btn,
  .sb-chevron,
  .sb-group__label {
    transition: none;
  }
}
</style>
